import { computed, type ComputedRef } from "vue";
import {
  chapterHeadings,
  chapterSceneLabel,
  type ChapterHeading,
  defaultChapterNumbering,
} from "../lib/chapterNumbering";
import { type Maybe, isJust, matchMaybe, toMaybe } from "../lib/maybe";
import { chapterName } from "../lib/zwConfig";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";

export function useChapterHeadings(): {
  headings: ComputedRef<ChapterHeading[]>;
  activeSceneLabel: ComputedRef<string>;
  actRenamePrefix: (folder: string) => string;
  chapterRenamePrefix: (folder: string) => string;
  actLabel: (folder: string) => string;
  canRenameAct: (folder: string) => boolean;
  headingFor: (folder: string) => Maybe<ChapterHeading>;
  chapterLabel: (folder: string) => string;
  canRenameChapter: (folder: string) => boolean;
} {
  const session = useEditorSessionStore();
  const headings = computed(() =>
    chapterHeadings(
      session.chapters,
      isJust(session.projectConfig)
        ? session.projectConfig.value.chapterNumbering
        : defaultChapterNumbering(),
      isJust(session.projectConfig)
        ? session.projectConfig.value.chapterNumberingOverrides
        : {},
    ),
  );
  const actHeadings = computed(() =>
    chapterHeadings(
      session.manuscript.filter((item) => item.type === "act"),
      isJust(session.projectConfig)
        ? session.projectConfig.value.actNumbering
        : defaultChapterNumbering(),
      {},
      "Act",
    ),
  );
  function actRenamePrefix(folder: string): string {
    return (
      actHeadings.value.find((heading) => heading.folder === folder)
        ?.renamePrefix ?? ""
    );
  }
  function chapterRenamePrefix(folder: string): string {
    return matchMaybe(headingFor(folder), {
      just: (heading) => heading.renamePrefix,
      nothing: () => "",
    });
  }
  function actLabel(folder: string): string {
    return (
      actHeadings.value.find((heading) => heading.folder === folder)?.text ??
      "Untitled act"
    );
  }
  function canRenameAct(folder: string): boolean {
    return (
      actHeadings.value.find((heading) => heading.folder === folder)
        ?.canRename ?? true
    );
  }
  function headingFor(folder: string): Maybe<ChapterHeading> {
    return toMaybe(headings.value.find((heading) => heading.folder === folder));
  }
  function chapterLabel(folder: string): string {
    return matchMaybe(headingFor(folder), {
      just: (heading) => heading.text,
      nothing: () => chapterName(session.chapters, folder),
    });
  }
  function canRenameChapter(folder: string): boolean {
    return matchMaybe(headingFor(folder), {
      just: (heading) => heading.canRename,
      nothing: () => true,
    });
  }
  const activeSceneLabel = computed(() => {
    if (
      isJust(session.trashPreview) ||
      isJust(session.activeSceneInfo) === false
    )
      return "Scene details";
    const info = session.activeSceneInfo.value;
    return chapterSceneLabel(
      info.chapter,
      info.sceneFile,
      chapterLabel(info.folder),
    );
  });
  return {
    actRenamePrefix,
    chapterRenamePrefix,
    activeSceneLabel,
    headings,
    headingFor,
    chapterLabel,
    canRenameChapter,
    actLabel,
    canRenameAct,
  };
}
